import React, { useState, useCallback, useEffect } from "react";
import { useDropzone } from "react-dropzone";
import Papa from "papaparse";
import axios from "axios";
import {
  FileSpreadsheet,
  Upload,
  Send,
  Loader2,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  FileText,
  Info,
} from "lucide-react";

import ProcessingModal from "../components/UploadCSV/ProcessingModal";
import SuccessModal from "../components/UploadCSV/SuccessModal";
import FailureModal from "../components/UploadCSV/FailureModal";

export default function UploadCSV() {
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [csvData, setCsvData] = useState([]);
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fileName, setFileName] = useState("");
  const [progress, setProgress] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const [showProcessingModal, setShowProcessingModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showFailureModal, setShowFailureModal] = useState(false);
  const [processedRows, setProcessedRows] = useState(0);

  const onDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    if (!file) return;

    if (file.type !== "text/csv" && !file.name.endsWith(".csv")) {
      setErrors([{ message: "Invalid file type. Please upload a CSV file." }]);
      return;
    }

    setFileName(file.name);

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (result) => {
        if (result.errors.length) {
          setErrors(result.errors.map((err) => ({ message: err.message })));
        } else {
          setCsvData(result.data);
          setErrors([]);
        }
      },
      error: (err) => {
        setErrors([{ message: `Failed to parse CSV: ${err.message}` }]);
      },
    });
  }, []);

  useEffect(() => {
    fetchProjects("1");
  }, []);

  const { getRootProps, getInputProps, isDragActive, isDragReject } =
    useDropzone({
      onDrop,
      accept: {
        "text/csv": [".csv"],
      },
      maxFiles: 1,
    });
  const fetchProjects = async (orgId) => {
    try {
      const response = await fetch(`${BASE_URL}/orgdetails/projects`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ org_id: orgId }),
      });
      if (!response.ok) throw new Error("Failed to fetch projects");
      const data = await response.json();
      setProjects(data);
    } catch (err) {
      setProjects([]);
      console.error(err);
    }
  };

  const processApiCall = async () => {
    if (csvData.length === 0) return;

    setLoading(true);
    setErrors([]);
    setProgress(0);
    setProcessedRows(0);
    setShowProcessingModal(true);

    const newErrors = [];
    let processedRowCount = 0;

    for (let i = 0; i < csvData.length; i++) {
      const row = csvData[i];
      const name = String(row["Name"] || "").trim();
      let phone = String(row["Phone"] || "").trim();
      const project_id = selectedProject?.id || "";
      const project_name = selectedProject?.project_name || "";

      if (phone.startsWith("+")) {
        phone = phone.slice(1);
      }

      const payload = {
        phone_number: phone,
        customer_name: name,
        project_name: project_name,
        template_name: "welcome_to_kns_family",
        project_id: project_id,
        organization_id: "1",
      };

      try {
        await new Promise((resolve) => setTimeout(resolve, 100));
        await axios.post(
          `${BASE_URL}/conversations/send-wp-notification`,
          payload
        );
        processedRowCount++;
        setProcessedRows(processedRowCount);
        setProgress(Math.round((processedRowCount / csvData.length) * 100));
      } catch (error) {
        newErrors.push({ message: `Row ${i + 1} (${name}): ${error.message}` });
      }
    }

    setLoading(false);
    setShowProcessingModal(false);

    if (newErrors.length > 0) {
      setErrors(newErrors);
      setShowFailureModal(true);
    } else {
      setShowSuccessModal(true);
    }
  };

  const totalPages = Math.ceil(csvData.length / rowsPerPage);
  const startRow = (currentPage - 1) * rowsPerPage;
  const endRow = Math.min(startRow + rowsPerPage, csvData.length);
  const currentData = csvData.slice(startRow, endRow);

  return (
    <div className="min-h-screen bg-blue-50 flex flex-col antialiased text-blue-900">
      <main className="flex-1 container mx-auto px-4 py-6 md:py-10">
        <div className="flex flex-col gap-6">
          {/* Upload Box */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-blue-800 mb-1">
              Select Project
            </label>
            <select
              className="w-full border border-blue-300 rounded-md px-3 py-2 text-sm text-blue-900"
              value={selectedProject?.id || ""}
              onChange={(e) => {
                const project = projects.find((p) => p.id == e.target.value);
                setSelectedProject(project);
              }}
            >
              <option value="" disabled>
                -- Choose a project --
              </option>
              {projects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.project_name}
                </option>
              ))}
            </select>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-blue-100 overflow-hidden">
            <div className="p-6">
              <h2 className="text-xl font-semibold text-blue-900 mb-2">
                Upload CSV File
              </h2>
              <p className="text-blue-700 mb-6">
                Upload CSV files containing data in required format
              </p>

              <div
                {...getRootProps()}
                className={`border-2 border-dashed rounded-lg p-8 flex flex-col items-center justify-center cursor-pointer ${
                  isDragActive
                    ? "border-blue-400 bg-blue-50"
                    : "border-blue-200 hover:border-blue-300 hover:bg-blue-50/50"
                } ${isDragReject ? "border-red-400 bg-red-50" : ""}`}
              >
                <input {...getInputProps()} />
                <div
                  className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 ${
                    isDragActive
                      ? "bg-blue-100 text-blue-600"
                      : "bg-blue-50 text-blue-500"
                  } ${isDragReject ? "bg-red-100 text-red-500" : ""}`}
                >
                  {isDragReject ? (
                    <FileText className="w-8 h-8" />
                  ) : (
                    <Upload className="w-8 h-8" />
                  )}
                </div>
                <div className="text-center">
                  <p className="font-medium text-blue-800 mb-2">
                    {isDragReject
                      ? "This file type is not supported"
                      : isDragActive
                      ? "Drop your CSV file here"
                      : "Drag & drop your CSV file here"}
                  </p>
                  <p className="text-sm text-blue-600 mb-4">or</p>
                  <button
                    type="button"
                    className="px-4 py-2 border border-blue-300 text-sm font-medium rounded-md text-blue-700 bg-white hover:bg-blue-50"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Browse Files
                  </button>
                  <p className="text-xs text-blue-500 mt-4">
                    Accepts CSV files only (.csv)
                  </p>
                </div>
              </div>

              {fileName && (
                <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-100 flex items-start">
                  <Info className="w-5 h-5 text-blue-500 mt-0.5 mr-2 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-blue-800">
                      Current file:{" "}
                      <span className="font-semibold">{fileName}</span>
                    </p>
                    <p className="text-xs text-blue-600 mt-1">
                      {csvData.length} rows loaded successfully
                    </p>
                  </div>
                </div>
              )}
            </div>

            {errors.length > 0 && !showFailureModal && (
              <div className="p-4 bg-red-50 border-t border-red-100">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-500 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-red-700 mb-2">
                      Error{errors.length > 1 ? "s" : ""}
                    </h3>
                    <ul className="list-disc list-inside text-sm text-red-700 space-y-1">
                      {errors.map((error, index) => (
                        <li key={index}>{error.message}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Data Table */}
          {csvData.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-blue-100 overflow-hidden">
              <div className="p-6 border-b border-blue-100">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div>
                    <h2 className="text-xl font-semibold text-blue-900 mb-1">
                      Data Preview
                    </h2>
                    <p className="text-blue-700 text-sm">
                      Showing rows {startRow + 1}-{endRow} of {csvData.length}
                    </p>
                  </div>
                  <div className="flex flex-col items-end">
                    <button
                      onClick={processApiCall}
                      disabled={loading || !selectedProject}
                      className="inline-flex items-center px-4 py-2 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 relative overflow-hidden"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          <span>Processing...</span>
                          <div
                            className="absolute bottom-0 left-0 h-1 bg-white/30"
                            style={{ width: `${progress}%` }}
                          />
                        </>
                      ) : (
                        <>
                          <Send className="mr-2 h-4 w-4" />
                          <span>Process Lead</span>
                        </>
                      )}
                    </button>
                    {loading && progress > 0 && (
                      <p className="text-xs text-blue-600 mt-2">
                        {progress}% complete
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-blue-50/70 text-blue-900">
                    <tr>
                      {Object.keys(csvData[0] || {}).map((header, idx) => (
                        <th
                          key={idx}
                          className="py-3 px-4 text-left font-medium text-sm border-b border-blue-100"
                        >
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-blue-100">
                    {currentData.map((row, rowIdx) => (
                      <tr
                        key={rowIdx}
                        className="hover:bg-blue-50/40 transition-colors"
                      >
                        {Object.values(row).map((value, colIdx) => (
                          <td
                            key={`${rowIdx}-${colIdx}`}
                            className="py-3 px-4 text-sm text-blue-800"
                          >
                            {value}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div className="flex justify-between items-center p-4 border-t border-blue-100 bg-white">
                  <div className="text-sm text-blue-600">
                    Page {currentPage} of {totalPages}
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() =>
                        setCurrentPage((prev) => Math.max(prev - 1, 1))
                      }
                      disabled={currentPage === 1}
                      className="px-3 py-2 border border-blue-300 text-sm rounded-md text-blue-700 bg-white hover:bg-blue-50 disabled:opacity-50"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() =>
                        setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                      }
                      disabled={currentPage === totalPages}
                      className="px-3 py-2 border border-blue-300 text-sm rounded-md text-blue-700 bg-white hover:bg-blue-50 disabled:opacity-50"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </main>

      {/* Modals */}
      <ProcessingModal
        isOpen={showProcessingModal}
        onClose={() => setShowProcessingModal(false)}
        progress={progress}
        totalRows={csvData.length}
      />
      <SuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        processedRows={processedRows}
        fileName={fileName}
      />
      <FailureModal
        isOpen={showFailureModal}
        onClose={() => setShowFailureModal(false)}
        errors={errors}
        processedRows={processedRows}
        totalRows={csvData.length}
      />
    </div>
  );
}
